// Sensitive-action confirm dialog shared by the devices / api-keys features
// (LOCKED rule: ban / impersonate / force-logout / device disable / report-lost /
// api-key revoke require an explicit confirm stating the consequence). Self-contained
// modal — no portal lib needed; styled by _shared/shared.css (.adm-modal*).
//
// API matches features/users/ConfirmDialog.tsx so the two can later be unified into
// one components/ConfirmDialog (shared-change request returned to the scaffold owner).

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export type ConfirmTone = 'danger' | 'warn' | 'primary';

export interface ConfirmDialogProps {
  title: string;
  /** The consequence statement — required by the LOCKED sensitive-action rule. */
  body: ReactNode;
  confirmLabel: string;
  tone?: ConfirmTone;
  /** Extra prominent banner (e.g. report-lost / revoke irreversibility warning). */
  warning?: ReactNode;
  busy?: boolean;
  /** Disables ONLY the confirm button (e.g. a required field in `body` is still empty). Cancel/Escape stay available. */
  confirmDisabled?: boolean;
  /** Optional inline error shown inside the dialog (e.g. the action call failed). */
  error?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({ title, body, confirmLabel, tone = 'primary', warning, busy, confirmDisabled, error, onConfirm, onCancel }: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const confirmClass = tone === 'danger' ? 'btn-danger' : tone === 'warn' ? 'btn-ghost' : 'btn-primary';

  return (
    <div className="adm-modal-scrim" role="presentation" onClick={() => !busy && onCancel()}>
      <div className="adm-modal card" role="alertdialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2 className="adm-modal-title">{title}</h2>
        {warning && <div className="adm-modal-warning">{warning}</div>}
        <div className="adm-modal-body text-secondary">{body}</div>
        {error && (
          <p className="error-text" style={{ margin: 0 }}>
            {error}
          </p>
        )}
        <div className="adm-modal-actions">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>
            取消
          </button>
          <button ref={confirmRef} className={confirmClass} onClick={onConfirm} disabled={busy || confirmDisabled}>
            {busy ? '處理中…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
